/**
 * Review & Rating Service
 * Manages customer reviews, ratings, and feedback synchronization with Cloudflare D1.
 */

export interface CustomerReview {
  id: string;
  bookingRef?: string;
  customerName: string;
  rating: number; // 1 to 5
  reviewText: string;
  serviceNames?: string;
  area?: string;
  isVerified: boolean;
  createdAt: string;
}

export interface ReviewStats {
  averageRating: number;
  totalReviews: number;
  starCounts: Record<number, number>;
}

const REVIEWS_STORAGE_KEY = 'blishka_customer_reviews';
const REVIEWS_STATS_STORAGE_KEY = 'blishka_reviews_stats';
export const REVIEWS_UPDATED_EVENT = 'blishka-reviews-updated';

export const DEFAULT_REVIEWS: CustomerReview[] = [
  {
    id: 'rev-seed-1',
    bookingRef: 'BL-98214',
    customerName: 'Ananya Sharma',
    rating: 5,
    reviewText: 'Booked home salon for the first time in Bhilai and honestly it was so convenient! Pooja di came with a clean single-use disposable kit and did an amazing job with zero mess.',
    serviceNames: 'Lotus Bright Facial & Rica Waxing',
    area: 'Smriti Nagar, Bhilai',
    isVerified: true,
    createdAt: '2026-10-21 14:30:00'
  },
  {
    id: 'rev-seed-2',
    bookingRef: 'BL-98188',
    customerName: 'Priyanka Deshmukh',
    rating: 5,
    reviewText: 'Very professional, polite and on time. Threading was virtually painless and waxing was super clean. Best part is relaxing at home without parlour rush.',
    serviceNames: 'Full Arms Wax & Eyebrows',
    area: 'Nehru Nagar, Bhilai',
    isVerified: true,
    createdAt: '2026-10-20 11:15:00'
  },
  {
    id: 'rev-seed-3',
    bookingRef: 'BL-97942',
    customerName: 'Shweta Verma',
    rating: 5,
    reviewText: 'The relaxing head and shoulder massage relieved all my stress! Completely hygienic setup in my own bedroom. Will definitely book every month.',
    serviceNames: 'Fruit Cleanup & Relaxing Massage',
    area: 'Civic Center, Bhilai',
    isVerified: true,
    createdAt: '2026-10-18 16:45:00'
  }
];

export const DEFAULT_STATS: ReviewStats = {
  averageRating: 4.9,
  totalReviews: 15,
  starCounts: { 5: 14, 4: 1, 3: 0, 2: 0, 1: 0 }
};

export function emitReviewsUpdate() {
  if (typeof window !== 'undefined') {
    window.dispatchEvent(new CustomEvent(REVIEWS_UPDATED_EVENT));
  }
}

/**
 * Returns the reviews list synchronously from localStorage, falling back to DEFAULT_REVIEWS.
 */
export function getReviewsList(): CustomerReview[] {
  if (typeof window === 'undefined') {
    return DEFAULT_REVIEWS;
  }
  try {
    const raw = localStorage.getItem(REVIEWS_STORAGE_KEY);
    if (!raw) {
      localStorage.setItem(REVIEWS_STORAGE_KEY, JSON.stringify(DEFAULT_REVIEWS));
      return DEFAULT_REVIEWS;
    }
    const parsed = JSON.parse(raw);
    if (Array.isArray(parsed) && parsed.length > 0) {
      return parsed;
    }
    return DEFAULT_REVIEWS;
  } catch {
    return DEFAULT_REVIEWS;
  }
}

/**
 * Returns review stats synchronously.
 */
export function getReviewStats(): ReviewStats {
  if (typeof window === 'undefined') {
    return DEFAULT_STATS;
  }
  try {
    const raw = localStorage.getItem(REVIEWS_STATS_STORAGE_KEY);
    if (raw) {
      return JSON.parse(raw);
    }
  } catch {
    // fallback
  }
  return DEFAULT_STATS;
}

/**
 * Fetches latest reviews from /api/reviews and syncs into localStorage.
 */
export async function syncReviewsFromRemote(): Promise<{
  success: boolean;
  reviews: CustomerReview[];
  stats: ReviewStats;
}> {
  try {
    const res = await fetch('/api/reviews', {
      headers: { 'Cache-Control': 'no-store' }
    });
    if (res.ok) {
      const data = await res.json() as {
        success?: boolean;
        reviews?: CustomerReview[];
        stats?: ReviewStats;
      };
      if (data && Array.isArray(data.reviews) && data.reviews.length > 0) {
        localStorage.setItem(REVIEWS_STORAGE_KEY, JSON.stringify(data.reviews));
        if (data.stats) {
          localStorage.setItem(REVIEWS_STATS_STORAGE_KEY, JSON.stringify(data.stats));
        }
        emitReviewsUpdate();
        return {
          success: true,
          reviews: data.reviews,
          stats: data.stats || DEFAULT_STATS
        };
      }
    }
  } catch (err) {
    console.warn('Could not sync reviews from /api/reviews:', err);
  }

  return {
    success: false,
    reviews: getReviewsList(),
    stats: getReviewStats()
  };
}

/**
 * Submits a new review and rating (by customer or Super Admin).
 * Updates local cache immediately for optimistic UI, then posts to /api/reviews.
 */
export async function submitReview(
  payload: {
    bookingRef?: string;
    customerName: string;
    customerPhone?: string;
    rating: number;
    reviewText: string;
    serviceNames?: string;
    area?: string;
    isVerified?: boolean;
  },
  adminPin?: string
): Promise<CustomerReview> {
  const newReview: CustomerReview = {
    id: `rev-${Date.now()}-${Math.random().toString(36).substring(2, 6)}`,
    bookingRef: payload.bookingRef,
    customerName: payload.customerName || 'Verified Customer',
    rating: Math.min(5, Math.max(1, payload.rating || 5)),
    reviewText: payload.reviewText.trim(),
    serviceNames: payload.serviceNames,
    area: payload.area || 'Bhilai / Durg',
    isVerified: payload.isVerified !== undefined ? payload.isVerified : true,
    createdAt: new Date().toISOString()
  };

  // 1. Optimistic local update
  try {
    const current = getReviewsList();
    const updated = [newReview, ...current.filter(r => r.id !== newReview.id)];
    localStorage.setItem(REVIEWS_STORAGE_KEY, JSON.stringify(updated));

    // Update stats
    const stats = getReviewStats();
    stats.totalReviews = Math.max(15, updated.length);
    stats.starCounts[newReview.rating] = (stats.starCounts[newReview.rating] || 0) + 1;
    localStorage.setItem(REVIEWS_STATS_STORAGE_KEY, JSON.stringify(stats));

    emitReviewsUpdate();
  } catch (e) {
    console.warn('Error saving review to localStorage:', e);
  }

  // 2. Asynchronous API POST
  try {
    const headers: Record<string, string> = { 'Content-Type': 'application/json' };
    if (adminPin) {
      headers['x-admin-pin'] = adminPin;
    }
    const res = await fetch('/api/reviews', {
      method: 'POST',
      headers,
      body: JSON.stringify(payload)
    });
    if (res.ok) {
      const data = await res.json() as any;
      if (data?.review) {
        return data.review;
      }
    }
  } catch (err) {
    console.warn('Failed to post review to /api/reviews:', err);
  }

  return newReview;
}

/**
 * Super Admin deletes a review
 */
export async function deleteReview(reviewId: string, adminPin?: string): Promise<boolean> {
  // 1. Optimistic local update
  try {
    const current = getReviewsList();
    const filtered = current.filter(r => r.id !== reviewId);
    localStorage.setItem(REVIEWS_STORAGE_KEY, JSON.stringify(filtered));

    const stats = getReviewStats();
    stats.totalReviews = Math.max(15, filtered.length);
    localStorage.setItem(REVIEWS_STATS_STORAGE_KEY, JSON.stringify(stats));

    emitReviewsUpdate();
  } catch (e) {
    console.warn('Error deleting review from localStorage:', e);
  }

  // 2. Asynchronous API DELETE
  try {
    const headers: Record<string, string> = {};
    if (adminPin) {
      headers['x-admin-pin'] = adminPin;
    }
    const res = await fetch(`/api/reviews?id=${encodeURIComponent(reviewId)}`, {
      method: 'DELETE',
      headers
    });
    return res.ok;
  } catch (err) {
    console.warn('Failed to delete review on server:', err);
    return false;
  }
}
